import { defineStore } from 'pinia'
import api from '../services/axios'
import { ref, computed } from 'vue'

export const useUserStore = defineStore(
  'user',
  () => {
    const user = ref(null)

    // --- 1. СОВМЕСТИМОСТЬ С VUESTIC UI ---
    // Vuestic ожидает эти поля для отображения в шапке (Header/Profile)
    const userName = computed(() => {
      if (!user.value) return 'Guest'
      return `${user.value.name || ''} ${user.value.surname || ''}`.trim() || user.value.email
    })
    const email = computed(() => user.value?.email || '')
    // Если у пользователя есть аватарка в БД, выводим её, иначе стандартную заглушку Vuestic
    const pfp = computed(() => user.value?.avatar_url || 'https://picsum.photos/id/22/200/300')
    const is2FAEnabled = ref(false)

    // --- 2. АВТОРИЗАЦИЯ И РОЛИ ---
    const isAuthenticated = computed(() => !!user.value)
    const userRole = computed(() => user.value?.role || 'guest')
    const userPermissions = computed(() => user.value?.permissions || [])

    // --- 3. ACTIONS ---
    const fetchUser = async () => {
      try {
        const response = await api.get('/api/user')
        user.value = response.data.data || response.data

        // Проверка на права доступа в админку
        if (!user.value.roles || user.value.roles.length === 0) {
          throw new Error('NOT_STAFF')
        }
      } catch (e) {
        user.value = null // Если бэкенд ответил 401, очищаем стор
        throw e
      }
    }

    const can = (permissionName) => {
      if (!user.value) return false
      if (user.value.roles?.includes('owner')) return true
      return userPermissions.value.includes(permissionName)
    }

    const login = async (credentials) => {
      try {
        await api.get('/sanctum/csrf-cookie')
        // Отправляем credentials. Если там есть remember: true, Laravel это подхватит
        await api.post('/login', credentials)
        await fetchUser()
        return true
      } catch (e) {
        user.value = null
        console.error('Login error:', e)
        throw e
      }
    }

    const register = async (formData) => {
      try {
        await api.get('/sanctum/csrf-cookie')
        await api.post('/register', formData)
        await fetchUser()
        return true
      } catch (e) {
        console.error(e)
        throw e
      }
    }

    const logout = async () => {
      try {
        await api.post('/logout')
      } catch (e) {
        console.error(e)
      } finally {
        // Очищаем стейт в любом случае, чтобы UI разлогинился
        user.value = null
        localStorage.removeItem('user') // Очистка persisted state
      }
    }

    const updateProfile = async (formData) => {
      try {
        const response = await api.post('/api/profile', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        if (response) {
          user.value = response.data.data || response.data
          return user.value
        }
      } catch (e) {
        console.error(e)
        throw e
      }
    }

    const updateAvatar = async (formData) => {
      try {
        const response = await api.post('/api/avatar', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        // ИСПРАВЛЕНИЕ: в setup-сторе нельзя использовать this.user
        user.value = response.data.user || response.data.data
        return true
      } catch (e) {
        console.error(e)
      }
    }

    const toggle2FA = () => {
      is2FAEnabled.value = !is2FAEnabled.value
    }

    const notifyTabsToUpdate = () => {
      const channel = new BroadcastChannel('user_updates')
      channel.postMessage({ type: 'USER_UPDATED' })
      channel.close()
    }

    const sendVerificationEmail = async () => {
      try {
        if (user.value?.email_verified_at) return false
        await api.post('/email/verification-notification')
        return true
      } catch (e) {
        console.error(e)
        return false
      }
    }

    return {
      // State & Getters
      user,
      userName,
      email,
      pfp,
      is2FAEnabled,
      isAuthenticated,
      userRole,
      userPermissions,

      // Actions
      can,
      fetchUser,
      login,
      register,
      logout,
      updateProfile,
      updateAvatar,
      toggle2FA,
      notifyTabsToUpdate,
      sendVerificationEmail,
    }
  },
  {
    persist: true, // Сохраняем состояние в localStorage
  },
)
